import React, { useEffect, useRef, useState } from 'react';
import './metrology.scss';
import { useDispatch, useSelector } from 'react-redux';
import JSZip from 'jszip';
import fileDownload from 'js-file-download';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Loader from '../../../components/base/Loader/Loader';
import { getSystemData } from '../../System/reducer';
import { createScreenshot } from '../../Dashboard/actions';
import notificationDecorator, {
  errorNotification,
  successNotification,
} from '../../Notifications/notificationDecorator';
import Camera from '../../Dashboard/components/Camera';
import TextAreaWithLabel from '../../../components/fields/TextInput/TextAreaWithLabel';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { textStatusColorFormatter } from '../../../utils/formatters';
import { getResponseError, nameBySave } from '../../../utils/utils';
import { getWSData } from '../../WebSocket/reducer';
import { clearGPSSource, toggleSaveGPSSource } from '../../WebSocket/actions';
import { getCamerasData } from '../../Settings/reducer';
import FilterRow from '../../../components/base/FilterRow/FilterRow';

export default function MetrologyMaster() {
  const dispatch = useDispatch();
  const [selectedCameras, setSelectedCameras] = useState(null);
  const blockRef = useRef(null);
  const { cameraList = [], loadingAllNodeEvent } = useSelector(getCamerasData);
  const { status } = useSelector(getSystemData);
  const [pf, setPF] = useState([]);
  const navigate = useNavigate();
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    gpsSources = [], wsStatus, saveGPS, latitude, longitude, gps_status,
  } = useSelector(getWSData);

  const {
    meteorological_name = '',
    meteorological_version = '',
    meteorological_hash = '',
  } = status;

  const selectCamera = id => {
    setSelectedCameras(id);
  };

  const gpsText = latitude && longitude
    ? `${Number(latitude).toFixed(6)} ${Number(longitude).toFixed(6)}`
    : formatMessage({ id: 'gps.dont.load' });

  const createSnapshot = () => {
    const zipArch = new JSZip();
    const promises = [];

    const id = selectedCameras;
    if (!id) {
      notificationDecorator(
        {
          title: formatMessage({ id: 'session.create' }),
          message: formatMessage({ id: 'session.create.camera-not-selected' }),
          isError: true,
        },
      );
      return;
    }
    const p = dispatch(createScreenshot(id)).then(({ data, timestamp }) => {
      if (data) {
        const camera = cameraList.find(f => f.id === id);
        zipArch.file(nameBySave(`${camera.name}_${timestamp}.jpg`), data);
      }
    });
    promises.push(p);

    Promise.all(promises)
      .then(() => {
        zipArch.generateAsync({ type: 'blob' }).then(zipContent => {
          fileDownload(zipContent, 'screenshot.zip');
          successNotification(formatMessage({ id: 'camera.screenshot' }), formatMessage({ id: 'camera.screenshot.success' }));
        });
      })
      .catch(response => {
        errorNotification(formatMessage({ id: 'camera.screenshot' }), getResponseError(response));
      });
  };

  const createSession = () => {
    if (!selectedCameras) {
      errorNotification(formatMessage({ id: 'session.create' }), formatMessage({ id: 'session.create.camera-not-selected' }));
      return;
    }
    dispatch(createScreenshot(selectedCameras)).then(({ data, timestamp }) => {
      if (pf.length < 2) {
        setPF([...pf, { data, timestamp }]);
      } else {
        setPF([{ data, timestamp }]);
      }
    });
  };

  const handleToggleSaveGps = () => {
    dispatch(toggleSaveGPSSource(!saveGPS));
  };

  const handleClear = () => {
    dispatch(clearGPSSource());
  };

  const handleDownload = () => {
    fileDownload(gpsSources.join(''), 'gps-log.txt');
  };

  let duration = null;

  if (pf.length === 2) {
    duration = moment.duration(moment(pf[1].timestamp).diff(moment(pf[0].timestamp)));
  }

  return (
    <div className="page metrology-page">
      <Loader loading={loadingAllNodeEvent} global />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'metrology.master' })}</div>
        <FilterRow>
          <Button variant="secondary" onClick={() => { navigate('/metrology'); }}>
            {formatMessage({ id: 'menu.metrology' })}
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper frame">
        <div className="page-content p-3" ref={blockRef}>
          <div className="metrology-block">
            <div className="metrology-param">
              <div className="settings-view-row">
                <div className="settings-view-label">{formatMessage({ id: 'metrology.name' })} «K1.so»:</div>
                <div className="settings-view-value">{meteorological_name || ''}</div>
              </div>
              <div className="settings-view-row">
                <div className="settings-view-label">{formatMessage({ id: 'metrology.version' })}:</div>
                <div className="settings-view-value">{meteorological_version || ''}</div>
              </div>
              <div className="settings-view-row">
                <div className="settings-view-label">{formatMessage({ id: 'metrology.digital.id' })}:</div>
                <div className="settings-view-value">{meteorological_hash || ''}</div>
              </div>
              <div className="settings-view-row">
                <div className="settings-view-label">{formatMessage({ id: 'metrology.average.gps' })}:</div>
                <div className="settings-view-value">{gpsText || ''}</div>
              </div>
              <div className="mt-3">
                <Camera
                  cameraIndex={0}
                  cameraList={cameraList}
                  cameraWidth={700}
                  cameraHeight={395}
                  width1={700}
                  height1={395}
                  frameWidth={700}
                  frameHeight={395}
                  selectCamera={selectCamera}
                  selectedCameraId={selectedCameras}
                  hideSettings
                />
                <Button onClick={createSnapshot} className="mt-3">
                  <Icons customIcon icon="camera" className="icon" />
                </Button>
                <Button onClick={createSession} className="mt-3 mx-3">
                  <Icons customIcon icon="session" className="icon" />
                </Button>
              </div>
            </div>
            <div className="metrology-gps">
              <TextAreaWithLabel className="gps-data" label="GPS" inputProps={{ value: gpsSources.join(''), onChange: () => {} }} textarea />
              <div className="metrology-gps-button">
                <Button onClick={handleToggleSaveGps}>{formatMessage({ id: !saveGPS ? 'start' : 'stop' })}</Button>
                <Button onClick={handleDownload}>{formatMessage({ id: 'app.download' })}</Button>
                <Button onClick={handleClear}>{formatMessage({ id: 'app.clear' })}</Button>
                {formatMessage({ id: 'ws.connection.status' })}: {textStatusColorFormatter(formatMessage({ id: wsStatus === 2 ? 'ws.connection.status.disconnect' : wsStatus === 1 ? 'ws.connection.status.connecting' : 'ws.connection.status.connect' }), wsStatus)}{}

              </div>
            </div>
          </div>
          {pf.length ? <div className="f4 mt-3 mb-3">{formatMessage({ id: 'session' })}</div> : null}
          <div className="session-block">
            {pf.map((item, index) => (
              <div key={index} className="session-pf">
                {`${formatMessage({ id: 'session.pf' })} ${index + 1}`}
                <img src={URL.createObjectURL(item.data)} alt={`${formatMessage({ id: 'session.pf' })} ${index + 1}`} />
                {/* {moment(item.timestamp).format('YYYY-MM-DD HH:mm:ss:SSS')} */}
              </div>
            ))}
          </div>
          {pf.length === 2 && duration ? (
            <div className="f4 mt-3 mb-3">
              {formatMessage({ id: 'session.pf.interval' }, {
                hour: duration.get('hours'),
                minutes: duration.get('minutes'),
                seconds: duration.get('seconds'),
                milliseconds: duration.get('milliseconds'),
                iso: duration.toISOString(),
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
